/// <amd-module name="TFS/Admin/Groups/Common/Contracts" />

import { GroupPermissions, PermissionTrace, SubjectPermission, SubjectPermissions } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import {
    GroupsNavType, IPermissionPage, MemberOfOperation,
    MemberOperation,
    MembershipQuery,
    PermissionChangeStatus,
    PermissionsContentType,
    PermissionsNavType,
    SearchStatus
} from "TFS/Admin/Groups/Common/Constants";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { AadGraphMember, GraphGroup, GraphMember, GraphMembership, GraphServicePrincipal, GraphUser } from "VSS/Platform/Contracts/Graph";

import { IOperationStatus } from "TFS/Admin/Common/Constants/Constants";
import { TeamMember } from "VSS/Platform/Contracts/WebApi";
import { IIdentity } from "VSSUI/IdentityPicker";
import { ILinkProps } from "VSSUI/Link";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { IFilter } from "VSSUI/Utilities/Filter";

export interface IPermissionsViewState
    extends IPermissionsPageAwareState,
        IPermissionsGroupsAwareState,
        IUsersPivotAwareState,
        IServicePrincipalsPivotAwareState,
        IMemberOfPivotAwareState,
        IGroupMembersPivotAwareState,
        IMemberOfPivotAwareState,
        IPermissionsPivotAwareState { }

export interface IPermissionsPageAwareState {
    pageState: IPermissionsPageState;
    groupsState: IPermissionsGroupsState;
    usersState: IUsersPivotState;
    servicePrincipalsState: IServicePrincipalsPivotState;
    membersState: IMemberOfPivotAwareState;
}

export interface IPermissionsPageState {
    selectedGroup?: PermissionsGroup;
    selectedUser?: GraphUser;
    selectedServicePrincipal?: GraphServicePrincipal;
    selectedMember?: GraphUser;
    selectedContentType: PermissionsContentType;
    selectedNavType: PermissionsNavType;
    pushDetailsView: boolean;
    popDetailsView: boolean;
    refreshDetailsView: boolean;
    selectedIdentity?: IIdentity;
    isAddGroupDisabled: boolean;
}

/****** Users Pivot interfaces ******/
export interface IUsersPivotAwareState {
    usersState: IUsersPivotState;
}

export interface IUsersPivotState {
    data: IUsersPivotData;
    searchResult: ISearchResultCache;
    initComplete: boolean;
    messageCardText?: string;
    messageCardSeverity?: MessageCardSeverity;
    loading: boolean;
}

export interface IUsersPivotData {
    totalIdentityCount: number;
    identities: CustomGraphUser[];
    moreData: boolean;
    continuationToken?: string[];
}

export interface CustomGraphUser extends GraphUser {
    linkProps?: ILinkProps;
}

export const enum UsersOperation {
    Fetch = 0
}

/****** Members Pivot interfaces ******/
export interface IMembersPivotAwareState {
    membersState: IMembersPivotState;
}

export interface IMembersPivotState {
    data: IMembersPivotData;
    searchResult: ISearchResultCache;
    initComplete: boolean;
    messageCardText?: string;
    messageCardSeverity?: MessageCardSeverity;
    loading: boolean;
}

export interface IMembersPivotData {
    totalIdentityCount: number;
    identities: CustomGraphMember[];
    moreData: boolean;
    continuationToken?: string[];
}

export interface CustomGraphMember extends AadGraphMember {
    linkProps?: ILinkProps;
    applicationId?: string;
}

export const enum MembersOperation {
    Fetch = 0
}

/****** ServicePrincipals Pivot interfaces ******/
export interface IServicePrincipalsPivotAwareState {
    servicePrincipalsState: IServicePrincipalsPivotState;
}

export interface IServicePrincipalsPivotState {
    data: IServicePrincipalsPivotData;
    searchResult: ISearchResultCache;
    initComplete: boolean;
    messageCardText?: string;
    messageCardSeverity?: MessageCardSeverity;
    loading: boolean;
}

export interface IServicePrincipalsPivotData {
    totalIdentityCount: number;
    identities: CustomGraphServicePrincipal[];
    moreData: boolean;
    continuationToken?: string[];
}

export interface CustomGraphServicePrincipal extends GraphServicePrincipal {
    linkProps?: ILinkProps;
}

export const enum ServicePrincipalsOperation {
    Fetch = 0
}

/****** Permissions Pivot interfaces ******/
export interface IPermissionsPivotAwareState {
    permissions: IPermissionsPivotState;
}

export interface IPermissionsPivotState {
    data: IPermissionsPivotData;
    loading: boolean;
    messageCardText?: string;
    messageCardSeverity?: MessageCardSeverity;
    isProjectScope: boolean;
}

export interface IPermissionsPivotData {
    permissions?: SubjectPermissions;
    selectedPermission?: SubjectPermission;
    permissionTraces: { [id: string]: PermissionTrace };
    selectedPermissionChangeStatus: SelectedPermissionChangeStatus;
}

export interface IPermissionsGroupKey {
    [key: string]: string;
}

export interface IGroupMembers {
    groupDescriptor: string;
    totalMemberCount?: number;
    members?: GraphMember[];
    linkProps?: ILinkProps;
}

/****** Members Pivot interfaces ******/
export interface IGroupMembersPivotAwareState {
    groupMembersState: IGroupMembersPivotState;
}

// The reducer state
export interface IGroupMembersPivotState {
    searchResult: ISearchResultCache;
    identities: GraphMembership[];
    users: ObservableArray<SelectableGraphMember>;
    loading: boolean;
    selectedItem?: SelectableGraphMember;
    showDeleteDialog: boolean;
    showMessageCard: boolean;
    messageCardText: string;
    messageCardSeverity: MessageCardSeverity;
    showAddMemberPanel: boolean;
    invalidMembers: IIdentity[];
    validMembers: IIdentity[];
    isAadOrg: boolean;
    multiOperationStatuses: IOperationStatus[];
    lastOperation: MemberOperation;
    queryMembers: QueryMembers;
    supportServicePrincipals: boolean;
}

export type QueryMembers = {
    [query in MembershipQuery]?: ObservableArray<SelectableGraphMember>;
};

/****** MembersOf Pivot interfaces ******/
export interface IMemberOfPivotAwareState {
    memberOfState: IMemberOfPivotState;
}

export interface IMemberOfPivotState {
    identities: GraphMembership[];
    groups: ObservableArray<PermissionsGraphGroup>;
    loading?: boolean;
    searchResult: ISearchResultCache;
    showDeleteDialog: boolean;
    showMessageCard: boolean;
    messageCardText: string;
    messageCardSeverity: MessageCardSeverity;
    showAddMemberOfPanel: boolean;
    selectedItem?: PermissionsGraphGroup;
    selectGroupsPermissions: { [descriptor: string]: GroupPermissions };
    invalidMembers: IIdentity[];
    validMembers: IIdentity[];
    multiOperationStatuses: IOperationStatus[];
    lastOperation: MemberOfOperation;
}

export interface PermissionsGraphGroup extends GraphGroup {
    actionPermissions?: GroupPermissions;
    linkProps?: ILinkProps;
}

/****** Groups Pivot interfaces ******/
export interface IPermissionsGroupsAwareState {
    groupsState: IPermissionsGroupsState;
}

// The reducer state
export interface IPermissionsGroupsState {
    data: IPermissionsGroupsViewData;
    initComplete: boolean;
    showDeleteGroupDialog: boolean;
    showAddGroupPanel: boolean;
    selectedItem?: PermissionsGroup;
    searchResult: ISearchResultCache;
    groupOperationResultMessage: string;
    groupOperationSuccess: boolean;
    groupOperationSeverity: MessageCardSeverity;
    showMessageCard: boolean;
    loading: boolean;
    totalIdentityCount: number;
    identities: GraphGroup[];
    moreData: boolean;
    continuationToken?: string[];
    scopeDescriptor?: string;
    isProjectScope: boolean;
    isAadOrg: boolean;
    toMarkTimeToInteract: boolean;
    permissionsGroups: IIdentity[];
    multiOperationStatuses: IOperationStatus[];
    groupNameAlreadyExists: boolean;
    isAddGroupDisabled: boolean;
    supportServicePrincipals: boolean;
}

// The data
export interface IPermissionsGroupsViewData {
    totalGroupCount: number;
    errorMessage: string;
    groups: PermissionsGroup[];
    continuationToken?: string[];
    moreData: boolean;
    numberOfGroupsOnScreen: number;
}

export interface PermissionsGroup extends PermissionsGraphGroup {
    isAadGroup: boolean;
    isWellKnownGroup: boolean;
    updateImage: boolean;
    isDefaultTeam: boolean;
    members?: GraphMember[];
    membersCount?: number;
    entityId: string;
    entityType: string;
    sid: string;
}

export interface IGroupsViewData {
    totalIdentityCount: number;
    errorMessage: string;
    identities: PermissionsGroup[];
    collectionHostId: string;
    isProjectScope: boolean;
    moreData: boolean;
}

export interface SelectedPermissionChangeStatus {
    [id: string]: PermissionChangeStatus;
}

/****** Settings Pivot interfaces ******/

export interface IGroupsSettingsAwareState {
    settingsState: IGroupsSettingsState;
}

// The reducer state
export interface IGroupsSettingsState {
    showChangeImageDialog: boolean;
    isSaving: boolean;
    groupName: string;
    groupDescription: string;
    deleteAvatar: boolean;
    currentGroupAvatarUrl: string;
    isDirty: boolean;
    messageCardText: string;
    messageCardSeverity: MessageCardSeverity;
    deletedGroup?: GraphGroup;
    teamAdmins: TeamAdmin[];
    showAddAdminPanel: boolean;
}

// Groups page view contracts here
export interface IGroupsViewState
    extends IOrganizationGroupsAwareState,
        IGroupMembersPivotAwareState,
        IGroupsSettingsAwareState,
        IPermissionsPivotAwareState { }

export interface IOrganizationGroupsAwareState {
    pageState: IGroupsPageState;
    groupsState: IPermissionsGroupsState;
    usersState: IUsersPivotState;
    servicePrincipalsState: IServicePrincipalsPivotState;
    membersState: IMembersPivotState;
}

export interface IGroupsPageState {
    selectedNavType: GroupsNavType;
    selectedGroup?: PermissionsGroup;
    selectedUser?: GraphUser;
    selectedMember?: CustomGraphMember;
    selectedServicePrincipal?: GraphServicePrincipal;
    updatedAvatarUrl?: string;
    filterPlaceHolder?: string;
    scopeDescriptor?: string;
    isTeamsPage?: boolean;
    toMarkPermissionsTTI: boolean;
    isURLRedirect: boolean;
    showAddMemberPanel: boolean;
    pcaGroup?: PermissionsGroup;
    invalidMembers: IIdentity[];
    validMembers: IIdentity[];
    canEditProjectLevelInformation: boolean;
    permissionPage?: IPermissionPage;
    isCollectionNavigationAllowed: boolean;
}

export interface IPivotProps {
    // User and Groups pivot
    filter: IFilter;
}

export interface ISearchResultCache {
    keyword: string;
    resultItems: any[];
    status: SearchStatus;
    hasMore: boolean;
}

export interface SelectableGraphMember extends GraphMember {
    selected: ObservableValue<boolean>;
    isAdmin: boolean;
    linkProps?: ILinkProps;
    entityId: string;
    entityType: string;
    metaType: string
    applicationId: string;
}

export interface IGroupMembersViewData {
    identities: SelectableGraphMember[];
    totalIdentityCount: number;
    hasMore: boolean;
}

export interface TeamAdmin extends TeamMember {
    isMember: boolean;
}